// The Home page "Quick Reports": what each one is, which period choices it offers, and how its default CSV is requested.
// The viewer (QuickReportViewer) and the Home card both read this list.
import { localYmd } from "../utils/dateOnly";

const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

// ---- period choices -------------------------------------------------------------------------------------------
// each returns [from, to] as dates (local midnight)
const PAST = {
    today:     { label: "Today",          range: (t) => [t, t] },
    yesterday: { label: "Yesterday",      range: (t) => [addDays(t, -1), addDays(t, -1)] },
    last7:     { label: "Last 7 days",    range: (t) => [addDays(t, -6), t] },
    last30:    { label: "Last 30 days",   range: (t) => [addDays(t, -29), t] },
    mtd:       { label: "Month to date",  range: (t) => [startOfMonth(t), t] },
    lastmonth: { label: "Last month",     range: (t) => [new Date(t.getFullYear(), t.getMonth() - 1, 1), new Date(t.getFullYear(), t.getMonth(), 0)] },
    last3m:    { label: "Last 3 months",  range: (t) => [addDays(t, -89), t] },
    ytd:       { label: "Year to date",   range: (t) => [new Date(t.getFullYear(), 0, 1), t] },
    lastyear:  { label: "Last year",      range: (t) => [new Date(t.getFullYear() - 1, 0, 1), new Date(t.getFullYear() - 1, 11, 31)] },
};
const PAST_ORDER = ["today", "yesterday", "last7", "last30", "mtd", "lastmonth", "last3m", "ytd", "lastyear"];

const DUE = {                      // supplier invoices: due dates ahead (or already late)
    next7:    { label: "Next 7 days",     range: (t) => [t, addDays(t, 7)] },
    next14:   { label: "Next 14 days",    range: (t) => [t, addDays(t, 14)] },
    next30:   { label: "Next 30 days",    range: (t) => [t, addDays(t, 30)] },
    thismonth:{ label: "Rest of this month", range: (t) => [t, new Date(t.getFullYear(), t.getMonth() + 1, 0)] },
    overdue:  { label: "Overdue",         range: (t) => [new Date(2000, 0, 1), addDays(t, -1)] },
};
const DUE_ORDER = ["next7", "next14", "next30", "thismonth", "overdue"];

const REFILL = {
    around:  { label: "Due now (2 days ago to 3 days ahead)", range: (t) => [addDays(t, -2), addDays(t, 3)] },
    next7:   { label: "Next 7 days",     range: (t) => [t, addDays(t, 7)] },
    next30:  { label: "Next 30 days",    range: (t) => [t, addDays(t, 30)] },
    missed:  { label: "Missed (last 30 days)", range: (t) => [addDays(t, -30), addDays(t, -1)] },
};
const REFILL_ORDER = ["around", "next7", "next30", "missed"];

const EXPIRY_DAYS = [30, 60, 90, 180, 365];

const build = (set, order) => order.map((key) => ({ key, label: set[key].label }));

export const QUICK_REPORTS = [
    { key: "sales", cardLabel: "Sales Summary – Last 7 days", endpoint: "/api/reports/sales-summary-last7.csv",
      picker: "range", presets: build(PAST, PAST_ORDER), defaultPreset: "last7", sets: PAST, custom: true,
      // what to show: totals per day (default, readable for any period), totals per product, or every day-and-product line
      groups: [{ key: "day", label: "Totals by day" }, { key: "product", label: "Totals by product" }, { key: "detail", label: "Day and product (detail)" }],
      defaultGroup: "day" },
    { key: "lowstock", cardLabel: "Low Stock / Below Reorder", endpoint: "/api/reports/low-stock.csv", picker: "none" },
    { key: "expiring", cardLabel: "Expiring Stock Batches", endpoint: "/api/reports/expiring-batches.csv", picker: "days", days: EXPIRY_DAYS },
    { key: "top", cardLabel: "Top 10 Sellers (30d)", endpoint: "/api/reports/sellers.csv", fixed: { order: "top", limit: 10 },
      picker: "range", presets: build(PAST, PAST_ORDER), defaultPreset: "last30", sets: PAST, custom: true },
    { key: "bottom", cardLabel: "Bottom 10 Sellers (30d)", endpoint: "/api/reports/sellers.csv", fixed: { order: "bottom", limit: 10 },
      picker: "range", presets: build(PAST, PAST_ORDER), defaultPreset: "last30", sets: PAST, custom: true },
    { key: "payments", cardLabel: "Payments Collected – Last 7 days", endpoint: "/api/reports/payments-last7.csv",
      picker: "range", presets: build(PAST, PAST_ORDER), defaultPreset: "last7", sets: PAST, custom: true },
    { key: "refills", cardLabel: "Refill Reminders Due", endpoint: "/api/reports/refill-reminders-due.csv",
      picker: "range", presets: build(REFILL, REFILL_ORDER), defaultPreset: "around", sets: REFILL, custom: true },
    { key: "supplierdue", cardLabel: "Supplier Credit Pay Due", endpoint: "/api/reports/supplier-invoices-due-next7/",
      picker: "range", presets: build(DUE, DUE_ORDER), defaultPreset: "next7", sets: DUE, custom: true },
];

/** The query string for a report and a chosen period: what the viewer asks for and what the download asks for. */
export function reportParams(report, choice) {
    const params = { ...(report.fixed || {}) };
    if (report.groups && choice?.group && choice.group !== "detail") params.group = choice.group;
    if (report.picker === "range" && choice) {
        const set = report.sets[choice.preset];             // undefined while a previous report's choice is still in state
        const today = new Date(new Date().setHours(0, 0, 0, 0));
        const [from, to] = choice.preset === "custom" ? [choice.from, choice.to] : (set ? set.range(today) : report.sets[report.defaultPreset].range(today));
        if (from && to) { params.from_date = localYmd(from); params.to_date = localYmd(to); }
    } else if (report.picker === "days" && choice?.days) {
        params.days = choice.days;
    }
    return params;
}

export const defaultChoice = (report, orgExpiryDays) => {
    if (report.picker === "range") return { preset: report.defaultPreset, ...(report.groups ? { group: report.defaultGroup } : {}) };
    if (report.picker === "days") return { days: report.days.includes(orgExpiryDays) ? orgExpiryDays : (orgExpiryDays || 30) };
    return {};
};
